"use client";

import Link from "next/link";
import * as React from "react";
import { RightopenArrowIcon } from "@/components/icons";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { cn } from "@/src/lib/utils";
import { useTranslations } from "next-intl";

type IconComponent = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { size?: number; title?: string }
>;

export function NavMain({
  items,
}: {
  items: {
    title: string;
    icon?: IconComponent;
    isActive?: boolean;
    hidden?: boolean;
    href?: string;
    is_coming_soon?: boolean;
    items?: { title: string; href?: string; isActive?: boolean }[];
  }[];
}) {
  const t = useTranslations()
  const visible = items.filter((i) => !i.hidden);

  return (
    <SidebarGroup>
      <SidebarMenu>
        {visible.map((section) => {
          const link = section.href || section.items?.[0]?.href || "#";

          return (
            <SidebarMenuItem
              key={section.title}
              className="w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center relative"
            >
              <SidebarMenuButton
                asChild={!section.is_coming_soon}
                tooltip={section.title}
                isActive={section.isActive}
                disabled={section.is_coming_soon}
                className={cn(
                  "flex h-11 w-full items-center gap-2 rounded-md px-3 transition",
                  !section.isActive && "text-foreground hover:bg-accent",
                  section.is_coming_soon && "opacity-50 cursor-not-allowed"
                )}
              >
                {section.is_coming_soon ? (
                  <div className="flex h-full w-full items-center gap-2 group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:justify-center">
                    {section.icon ? (
                      <section.icon
                        size={16}
                        className="shrink-0 text-muted-foreground"
                      />
                    ) : null}
                    <span className="relative group-data-[collapsible=icon]:hidden">
                      {section.title}
                      <span className="absolute top-1/2 -right-2 translate-x-full -translate-y-1/2 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-md whitespace-nowrap font-medium">
                        {t("coming_soon_label")}
                      </span>
                    </span>
                  </div>
                ) : (
                  <Link href={link}>
                    {section.icon ? (
                      <section.icon
                        size={20}
                        className={cn(
                          "shrink-0",
                          section.isActive ? "text-white" : "text-muted-foreground"
                        )}
                      />
                    ) : null}

                    <span className="truncate group-data-[collapsible=icon]:hidden">{section.title}</span>

                    <RightopenArrowIcon
                      size={16}
                      className={cn(
                        "ms-auto shrink-0 rtl:rotate-180 group-data-[collapsible=icon]:hidden",
                        section.isActive ? "text-white" : "text-muted-foreground"
                      )}
                    />
                  </Link>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
